import { Card } from "@/components/ui/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Clock, CheckCircle, XCircle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useEffect } from "react";
import { fromZonedTime } from 'date-fns-tz';
import { useShopTimezone } from '@/contexts/TimezoneContext';

interface WaiterPromiseWidgetProps {
  shopId: string;
  dateISO?: string;
}

export function WaiterPromiseWidget({ shopId, dateISO }: WaiterPromiseWidgetProps) {
  const queryClient = useQueryClient();
  const { shopTimezone } = useShopTimezone();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['waiter-promise-stats', shopId, dateISO, shopTimezone],
    queryFn: async () => {
      let query = supabase
        .from('waiter_promise_tracking')
        .select('*')
        .eq('shop_id', shopId);

      if (dateISO) {
        // KPI-R71-FIX (HIGH-4): previously hard-coded `Z` suffix forced a UTC
        // interpretation of a shop-local date string. For a PST shop
        // (UTC-8) passing dateISO="2024-12-03", the old query read rows
        // with scheduled_start_time in `2024-12-03T00:00:00Z..23:59:59Z`,
        // which in PST is Dec 2 16:00 → Dec 3 15:59 — so the widget
        // rendered a day's worth of rows that SPANS the shop boundary
        // rather than the shop-local Dec 3. Convert through the shop TZ
        // so the window actually aligns with the shop's calendar day.
        const startUtc = fromZonedTime(`${dateISO}T00:00:00`, shopTimezone).toISOString();
        const endUtc = fromZonedTime(`${dateISO}T23:59:59`, shopTimezone).toISOString();
        query = query.gte('scheduled_start_time', startUtc).lte('scheduled_start_time', endUtc);
      }

      const { data, error } = await query;
      if (error) throw error;

      const total = data.length;
      const onTime = data.filter(p => p.on_time === true).length;
      const late = data.filter(p => p.on_time === false).length;
      const avgDelta = data.length > 0
        ? data.reduce((sum, p) => sum + (p.delta_minutes || 0), 0) / data.length
        : 0;

      return {
        total,
        onTime,
        late,
        onTimePercentage: total > 0 ? (onTime / total) * 100 : 0,
        avgDelta: Math.round(avgDelta)
      };
    }
  });

  // Realtime subscription
  useEffect(() => {
    // Chronomancer R-RT-4: scope channel name with shop id so two tabs
    // from different shops don't collide. Previously bare
    // `waiter-promise-changes` could multiplex across tenants in the
    // same browser session.
    const channel = supabase
      .channel(`waiter-promise-changes-${shopId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'waiter_promise_tracking',
          filter: `shop_id=eq.${shopId}`
        },
        () => {
          // IV-D-22 R73 FIX-1: include shopTimezone so the invalidation key
          // matches the query's key (line 20). Previously a realtime event
          // invalidated a non-existent cache entry and the widget showed
          // stale on-time/late counts until full page refresh.
          queryClient.invalidateQueries({ queryKey: ['waiter-promise-stats', shopId, dateISO, shopTimezone] });
        }
      )
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: surface drops so a stale promise widget
        // doesn't quietly mislead advisors on customer-promise status.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[WaiterPromiseWidget] realtime status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shopId, dateISO, shopTimezone, queryClient]);

  if (isLoading) {
    return <Card className="p-4"><div className="h-32 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!stats || stats.total === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Waiter Promise Tracking</h3>
        </div>
        <p className="text-sm text-muted-foreground">No waiter appointments tracked yet</p>
      </Card>
    );
  }

  const { total, onTime, late, onTimePercentage, avgDelta } = stats;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold text-foreground">Waiter Promise Tracking</h3>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">On-Time Rate</span>
            <span className="font-mono text-lg font-bold text-foreground">
              {onTimePercentage.toFixed(0)}%
            </span>
          </div>
          <Progress value={onTimePercentage} className="h-2" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2 p-2 bg-success/10 rounded">
            <CheckCircle className="h-4 w-4 text-success" />
            <div>
              <p className="text-xs text-muted-foreground">On Time</p>
              <p className="font-mono text-sm font-medium text-foreground">{onTime}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 p-2 bg-destructive/10 rounded">
            <XCircle className="h-4 w-4 text-destructive" />
            <div>
              <p className="text-xs text-muted-foreground">Late</p>
              <p className="font-mono text-sm font-medium text-foreground">{late}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-sm text-muted-foreground">Avg Delta</span>
          <span className={`font-mono text-sm font-medium ${avgDelta > 0 ? 'text-destructive' : 'text-success'}`}>
            {avgDelta > 0 ? '+' : ''}{avgDelta} min
          </span>
        </div>
      </div>
    </Card>
  );
}
